import { Box, Chip, CircularProgress, Container, Divider, Stack, Typography } from '@mui/material';
import { ClockIcon, MapPinIcon, UsersThreeIcon } from '@phosphor-icons/react';

import { usePublicOrgProfile, usePublicUnitIdentity, useMeetingSchedule } from '@/hooks/use-public-site-modules';
import { useSiteOrgId } from '@/lib/site-org-context';
import { getUnitTypeFullLabel } from '@/lib/seed-content';
import type { PublicMeetingSchedule, UnitIdentityBlockContent } from '@/types/database';

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatMeetingTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

// Bound entirely to the unit record — never manually typed. Unit name comes
// from org_profiles (site-builder's own snapshot of the Clerk org name,
// already public once the site is); type/number/council/district/chartered-org
// come from the shared unit_details record via the public gateway RPC (so
// they stay in sync with the hub's Details page); meeting schedule from the
// same gateway. "Never manually typed" is the whole point: a unit that never
// logs in still shows the correct Cubmaster-equivalent meeting info.
export function UnitIdentityBlockView({ content }: { content: UnitIdentityBlockContent }) {
  const orgId = useSiteOrgId();
  const { data: profile, isLoading: profileLoading } = usePublicOrgProfile(orgId);
  const { data: identity, isLoading: identityLoading } = usePublicUnitIdentity(orgId);
  const { data: schedule, isLoading: scheduleLoading } = useMeetingSchedule(orgId);
  const showMeetingSchedule = content.showMeetingSchedule !== false;

  if (profileLoading || identityLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} aria-label="Loading unit information" />
      </Box>
    );
  }

  if (!profile) return null;

  const unitType = identity?.unit_type ?? profile.unit_type;
  const charteredOrgName = identity?.chartered_org_name ?? null;
  const councilName = identity?.council_name ?? null;
  const districtName = identity?.district_name ?? null;

  return (
    <Box sx={{ py: 6, bgcolor: 'action.hover' }}>
      <Container maxWidth="sm">
        <Stack spacing={2.5} sx={{ alignItems: 'center', textAlign: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {profile.unit_name}
            </Typography>
            {unitType ? (
              <Typography color="text.secondary">{getUnitTypeFullLabel(unitType)}</Typography>
            ) : null}
          </Box>

          {charteredOrgName || councilName || districtName ? (
            <Stack spacing={0.5}>
              {charteredOrgName ? (
                <Typography variant="body2">Chartered by {charteredOrgName}</Typography>
              ) : null}
              {councilName || districtName ? (
                <Typography variant="body2" color="text.secondary">
                  {[councilName, districtName].filter(Boolean).join(' · ')}
                </Typography>
              ) : null}
            </Stack>
          ) : null}

          {showMeetingSchedule && !scheduleLoading && schedule && schedule.length > 0 ? (
            <>
              <Divider flexItem />
              <Stack spacing={1.5} sx={{ width: '100%' }}>
                {schedule.map((entry) => (
                  <MeetingRow key={entry.schedule_id} entry={entry} />
                ))}
              </Stack>
            </>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
}

function MeetingRow({ entry }: { entry: PublicMeetingSchedule }) {
  return (
    <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
      <Chip icon={<UsersThreeIcon size={14} />} label={entry.event_type_name} size="small" variant="outlined" />
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <ClockIcon size={16} />
        <Typography variant="body2">
          {capitalize(entry.day_of_week)}s at {formatMeetingTime(entry.meeting_time)}
        </Typography>
      </Stack>
      {entry.location_name || entry.formatted_address ? (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <MapPinIcon size={16} />
          <Typography variant="body2" color="text.secondary">
            {entry.location_name || entry.formatted_address}
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
}
